<script lang="ts" setup>
import type { KUICoreRegionSwitcherOption } from '~kui/components/KUI/Core/RegionSwitcher/KUICoreRegionSwitcher.vue';
import type { NavItem } from '~kui/types/navigation';

import { useUnifiedPublicLoaderContext } from '~kui/composables/useUnifiedPublicLoaderContext';

interface PublicNavItem extends NavItem {
  isSecondary?: boolean;
}

interface FooterLink {
  text: string;
  to: string;
  external?: boolean;
}

interface Props {
  navItems: PublicNavItem[];
  homeLink: string;
  logoLabel: string;
  upperFooterLinks: FooterLink[];
  lowerFooterLinks: FooterLink[];
  fullWidth?: boolean;
  fixedHeader?: boolean;
  hideNav?: boolean;
  /** Keep the header bar but show only the logo in it (see KUIUnifiedLayoutPublicNav). */
  logoOnlyNav?: boolean;
  footerRegions?: KUICoreRegionSwitcherOption[];
  /** The signed-in visitor's in-app motion switch, handed to the nav's menu. */
  animationsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  fixedHeader: false,
  hideNav: false,
  logoOnlyNav: false,
  footerRegions: () => [],
  animationsEnabled: true,
});

const emit = defineEmits<{
  regionSwitch: [region: KUICoreRegionSwitcherOption, event: MouseEvent];
}>();

const containerVariantClasses = computed(() => {
  return twMerge(cva(
    '',
    {
      variants: {
        fullWidth: {
          true: '',
          false: 'px-2 md:px-0 md:container md:mx-auto',
        },
      },
    },
  )({
    fullWidth: props.fullWidth,
  }))
})

const fixedHeaderClasses = computed(() => {
  return cva('', {
    variants: {
      fixedHeader: {
        true: 'fixed top-0 inset-x-0 z-40',
        false: 'relative z-40',
      },
    },
  })({
    fixedHeader: props.fixedHeader,
  });
})

const loaderCtx = useUnifiedPublicLoaderContext();
</script>

<template>
  <!-- overflow-x-clip, not -hidden: hidden makes this div a scroll container, -->
  <!-- which silently breaks position:sticky for descendants (e.g. the institution navbar). -->
  <div class="bg-background overflow-x-clip">
    <KUICoreSkipToMainContentButton href="#main-content" />
    <div v-if="!hideNav" :class="fixedHeaderClasses">
      <!-- A host with a bar of its own (one that also knows who is signed in) puts it here. -->
      <slot name="nav">
        <KUIUnifiedLayoutPublicNav
          :logo-label="logoLabel"
          :home-link="homeLink"
          :nav-items="navItems"
          :logo-only="logoOnlyNav"
          :animations-enabled="animationsEnabled"
        />
      </slot>
    </div>
    <!-- Visible by default (server-rendered, crawlable, works without JS); only hidden while
         the client-side intro is actively animating, then revealed on completion. -->
    <div v-show="!loaderCtx || !loaderCtx.loaderActive.value || loaderCtx.loaderComplete.value">
      <main id="main-content" :class="containerVariantClasses">
        <slot />
      </main>

      <KUICoreMainFooter
        :upper-links="upperFooterLinks"
        :lower-links="lowerFooterLinks"
        :copyright="$t('unified.footer.copyright')"
        :regions="footerRegions"
        logo-illustration="anyway-logo-beta"
        @region-switch="(region, event) => emit('regionSwitch', region, event)"
      />
    </div>
  </div>
</template>
